// Helper Functions
window.eventStreaming = window.eventStreaming || {};

window.eventStreaming.getPageType = (url) => {
  if (!url) {
    return "";
  }
  const urlWithoutQuery = url.split("?")[0];
  const isRoot = /^https?:\/\/[^\/]+\/?$/.test(urlWithoutQuery);

  if (isRoot) {
    return "homepage";
  } else if (/\/products\//.test(url)) {
    return "product page";
  } else if (/\/collections\//.test(url)) {
    return "collection page";
  } else {
    return "other";
  }
};

document.addEventListener("DOMContentLoaded", async function () {
  const getLocalStorageValue = (key) => {
    try {
      return window.localStorage?.getItem(key) ?? null;
    } catch (_) {
      return null;
    }
  };

  async function updateCartAttributes(deviceId) {
    try {
      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          attributes: {
            deviceId: deviceId,
            appVersion: getLocalStorageValue("appVersion") ?? "NA",
            webOrigin: getLocalStorageValue("isApp")
              ? "CUREFIT_APP"
              : "CULTSTORE_WEBSITE",
              "Order Source": "Shopify_normal",
          },
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Error updating cart attributes: ${response.statusText}`,
        );
      }

      const cartData = await response.json();
      console.log("Cart updated successfully:", cartData);
    } catch (error) {
      console.error("Error updating cart:", error);
    }
  }

  function flattenCart(cart) {
    let flattened = {
      token: cart.token,
      note: cart.note,
      originalTotalPrice: cart.original_total_price,
      totalPrice: cart.total_price,
      totalDiscount: cart.total_discount,
      totalWeight: cart.total_weight,
      itemCount: cart.item_count,
      requiresShipping: cart.requires_shipping,
      currency: cart.currency,
      itemsSubtotalPrice: cart.items_subtotal_price,
    };

    // Flatten attributes
    if (cart.attributes) {
      Object.keys(cart.attributes).forEach((key) => {
        flattened[`attribute_${key}`] = cart.attributes[key];
      });
    }

    // Flatten cart-level discounts
    flattened.cartLevelDiscounts =
      cart.cart_level_discount_applications.length > 0
        ? cart.cart_level_discount_applications
            .map((d, i) => `${i}: ${JSON.stringify(d)}`)
            .join(", ")
        : "None";

    // Flatten items
    let productTitles = [],
      productPrices = [],
      productIds = [],
      productUrls = [],
      productTypes = [],
      productTypesCombined = [],
      variantTitles = [],
      skus = [],
      quantities = [],
      images = [],
      vendors = [];

    cart.items.forEach((item, index) => {
      productTitles.push(`${index}: ${item.product_title}`);
      productPrices.push(`${index}: ${item.price}`);
      productIds.push(`${index}: ${item.product_id}`);
      productUrls.push(`${index}: ${item.url}`);
      productTypes.push(`${index}: ${item.product_type}`);
      productTypesCombined.push(`${item.product_type}`);
      variantTitles.push(`${index}: ${item.variant_title}`);
      skus.push(`${index}: ${item.sku}`);
      quantities.push(`${index}: ${item.quantity}`);
      images.push(`${index}: ${item.image}`);
      vendors.push(`${index}: ${item.vendor}`);
    });

    flattened.productTitles = productTitles.join(", ");
    flattened.productPrices = productPrices.join(", ");
    flattened.productIds = productIds.join(", ");
    flattened.productUrls = productUrls.join(", ");
    flattened.productTypes = productTypes.join(", ");
    flattened.productTypesCombined = productTypesCombined.join(", ");
    flattened.variantTitles = variantTitles.join(", ");
    flattened.skus = skus.join(", ");
    flattened.quantities = quantities.join(", ");
    flattened.images = images.join(", ");
    flattened.vendors = vendors.join(", ");

    return flattened;
  }

  function flattenNestedObject(value, prefix, target) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      target[prefix] = value;
      return;
    }

    Object.keys(value).forEach((key) => {
      const nextValue = value[key];
      const nextPrefix = `${prefix}___${key}`;

      if (
        nextValue &&
        typeof nextValue === "object" &&
        !Array.isArray(nextValue)
      ) {
        flattenNestedObject(nextValue, nextPrefix, target);
        return;
      }

      target[nextPrefix] = nextValue;
    });
  }

  updateCartAttributes(getCookieValue("_shopify_y"));

  // Helper function to dispatch custom events
  window.eventStreaming.dispatchCustomEvent = (eventName, data) => {
    console.log("Event: ", eventName, data);
    const event = new CustomEvent(eventName, {
      detail: data,
      bubbles: true,
      cancelable: true,
    });
    document.dispatchEvent(event);
  };

  function getCookieValue(cookieName) {
    const cookies = document.cookie.split("; ");
    for (let cookie of cookies) {
      const [name, value] = cookie.split("=");
      if (name === cookieName) {
        return decodeURIComponent(value);
      }
    }
    return null; // Return null if the cookie is not found
  }

  // If utm_parameters are present in the URL, get them and return
  function getUtmParameters() {
    const params = new URLSearchParams(window.location.search);
    const utmParams = {};
    for (const [key, value] of params.entries()) {
      if (key.includes("utm_")) {
        utmParams[key] = value;
      }
    }
    return utmParams;
  }

  function getCartMode(detail) {
    if (detail && typeof detail.cartMode === "string") {
      return detail.cartMode;
    }
    const reactEnabled = window?.RSLiquidData?.settings?.use_react_cart_drawer;
    return reactEnabled ? "react" : "legacy";
  }

  function getProductContext() {
    const customProductData =
      window.customProductData && typeof window.customProductData === "object"
        ? window.customProductData
        : null;
    const fallbackProductData =
      window.cultstore && typeof window.cultstore.productData === "object"
        ? window.cultstore.productData
        : null;
    const product =
      customProductData &&
      customProductData.product &&
      typeof customProductData.product === "object"
        ? customProductData.product
        : null;
    const selectedVariant =
      customProductData && customProductData.selectedVariant
        ? customProductData.selectedVariant
        : fallbackProductData && fallbackProductData.selectedVariant
          ? fallbackProductData.selectedVariant
          : null;
    const productCsId =
      customProductData && customProductData.productCsId
        ? customProductData.productCsId
        : fallbackProductData && fallbackProductData.productCsId
          ? fallbackProductData.productCsId
          : null;

    return {
      customProductData,
      fallbackProductData,
      product,
      selectedVariant,
      productCsId,
    };
  }

  function applyProductData(customData, productContext) {
    if (!productContext || !productContext.product) {
      return false;
    }

    const data = productContext.product;
    customData["product"] = data;
    if (data && typeof data === "object") {
      Object.keys(data).forEach((key) => {
        customData["pd_" + key] = data[key];
      });
    }
    customData["productHandle"] = data.handle;
    customData["productId"] = data.id;
    customData["csProductId"] = productContext.productCsId || "";
    customData["productTitle"] = data.title;
    customData["productFirstImage"] = data.image?.src;
    customData["productSellingPrice"] = data.variants?.[0]?.price;
    customData["productMrp"] = data.variants?.[0]?.compare_at_price;

    if (productContext.selectedVariant) {
      customData["selectedVariant"] = productContext.selectedVariant;
    }

    return true;
  }

  // Build shared analytics payloads for Breeze checkout and purchase events.
  window.eventStreaming.buildBreezeAnalyticsPayloads = ({
    items = [],
    totalPrice = 0,
    eventType = "",
    eventTime = new Date().toISOString(),
    sendTo = "G-ZWHZKTYB0D",
    transactionId,
    shipping,
  } = {}) => {
    const fbAnalyticsItems = [];
    const googleAnalyticsItems = [];

    items.forEach((item, idx) => {
      const quantity = item.quantity || 1;
      const variant = item.variantTitle || item.variant;
      const variantId = item.variant || variant;

      fbAnalyticsItems.push({
        id: item.id,
        quantity,
        item_price: item.price,
        item_name: item.name,
        item_variant: variant,
        currency: "INR",
        vendor: item.vendor,
        productType: item.productType,
        index: idx,
      });

      googleAnalyticsItems.push({
        item_id: `shopify_IN_${item.id}_${variantId}`,
        item_name: item.name,
        price: item.price,
        quantity,
        variant,
        item_brand: item.vendor,
        currency: "INR",
        vendor: item.vendor,
        productType: item.productType,
        index: idx,
      });
    });

    const googlePayload = {
      send_to: sendTo,
      currency: "INR",
      value: totalPrice || 0,
      items: googleAnalyticsItems,
      breeze_event_type: eventType,
      breeze_event_time: eventTime,
    };

    if (transactionId) {
      googlePayload.transaction_id = transactionId;
    }

    if (typeof shipping !== "undefined") {
      googlePayload.shipping = shipping;
    }

    const facebookPayload = {
      content_type: "product_group",
      value: totalPrice || 0,
      content_ids: fbAnalyticsItems.map((item) => item.id),
      contents: fbAnalyticsItems,
      num_items: fbAnalyticsItems.length,
      currency: "INR",
    };

    return {
      fbAnalyticsItems,
      googleAnalyticsItems,
      googlePayload,
      facebookPayload,
    };
  };

  // User Attibutes - USER_ATTRIBUTES if logged in
  const storedCustomer = getLocalStorageValue("customer");
  if (storedCustomer) {
    try {
      window.eventStreaming.dispatchCustomEvent("USER_ATTRIBUTES", JSON.parse(storedCustomer));
    } catch (_) {}
  }

  //Helper Functions
  // Prepare Common Details
  window.eventStreaming.prepareCommonDetails = () => {
    try {
      const metaData = window.eventStreaming.metaData;
      let commonFields = {
        ...metaData,
        pageUrl: window.location.href,
        utmParams: getUtmParameters(),
        previousPageUrl: document.referrer,
        screenHeight: window.screen.height,
        screenWidth: window.screen.width,
        webOrigin: getLocalStorageValue("isApp")
          ? "CUREFIT_APP"
          : "CULTSTORE_WEBSITE",
        userAgent: navigator.userAgent,
        appVersion: getLocalStorageValue("appVersion") ?? "",
        eventId: crypto.randomUUID() + "-" + crypto.randomUUID(),
        deviceId: getCookieValue("_shopify_y"),
        redirectDeviceId:
          getLocalStorageValue("1p_redirect_id") ||
          new URLSearchParams(window.location.search).get("utm_redirect_id") ||
          "",
        sessionId: getCookieValue("_shopify_s"),
        previousPageCategory: window.eventStreaming.getPageType(
          document.referrer,
        ),
        pageCategory: window.eventStreaming.getPageType(window.location.href),
      };
      // #TODO: Implement session id later
      // gtag('get', 'G-ZWHZKTYB0D', 'session_id', (id) => {
      //     commonFields.sessionId = id
      // });
      window.eventStreaming.latestCommonDetails = { ...commonFields };
      return { ...commonFields };
    } catch (err) {
      console.error(err);
      return err;
    }
  };

  //Fire On Page Load Events
  //Page View - PAGE_VIEW/ PDP_VIEW
  if (window.location.href.includes("products")) {
    // Fetch product details from the product page
    const customData = await window.eventStreaming.prepareCommonDetails();
    const productContext = getProductContext();
    const hasProductData = applyProductData(customData, productContext);

    // Probing for cases where previous page is from cultsport.com and customData does not have redirect_id
    if (
      !customData.redirectDeviceId &&
      document.referrer &&
      new URL(document.referrer).hostname.includes("cultsport.com")
    ) {
      console.error("Redirect Device ID not found, setting it to empty string");
    }

    window.eventStreaming.dispatchCustomEvent("PAGE_VIEW", customData);
    if (hasProductData) {
      window.eventStreaming.dispatchCustomEvent("PDP_VIEW", customData);
    } else {
      console.warn("Skipping PDP enrichment because product data is unavailable");
    }
  } else {
    // Fetch common details from the page
    const customData = await window.eventStreaming.prepareCommonDetails();
    window.eventStreaming.dispatchCustomEvent("PAGE_VIEW", customData);
  }

  //Button Click - BUTTON_CLICK
  document.querySelectorAll("button").forEach((button) => {
    button.addEventListener("click", async (e) => {
      const customData = await window.eventStreaming.prepareCommonDetails();
      customData["buttonName"] = e.target.getAttribute("data-event-name");
      customData["buttonAction"] = e.target.getAttribute("data-event-type");
      if(e.target.getAttribute("data-event-name") == "Pincode Check"){
        const pincodeInput = document.querySelector("#PincodeInput").value;
        customData["pincode"] = pincodeInput;
      }
      if(e.target.getAttribute("data-event-name") == "Captcha Button Click"){
        const customerPhone = document.querySelector("#phone-number").value;
        customData["customerPhone"] = customerPhone;
      }
      window.eventStreaming.dispatchCustomEvent("BUTTON_CLICK", customData);
    });
  });

  //Link Click - LINK_CLICK
  document.querySelectorAll("a").forEach((link) => {
    link.addEventListener("click", async (e) => {
      const customData = await window.eventStreaming.prepareCommonDetails();
      customData["linkName"] = e.target.getAttribute("data-event-name");
      customData["linkAction"] = e.target.getAttribute("data-event-type");
      window.eventStreaming.dispatchCustomEvent("LINK_CLICK", customData);
    });
  });

  //Apply Sort - APPLY_SORT
  async function handleSortChange() {
    let stateChanged = false;
    if (window.location.search.includes("sort_by=")) {
      const sortName = new URLSearchParams(window.location.search).get(
        "sort_by",
      );
      if (sortName !== previousState.sort) {
        customData["sortName"] = sortName;
        previousState.sort = sortName;
        stateChanged = true;
      }
    }

    if (stateChanged) {
      window.eventStreaming.dispatchCustomEvent("APPLY_SORT", customData);
    }
  }

  //Apply Filter - APPLY_FILTER, Apply Sort - APPLY_SORT
  // Function to parse URL parameters and filter those containing 'filter'
  function getFilterParams() {
    const params = new URLSearchParams(window.location.search);
    const filters = {};
    for (const [key, value] of params.entries()) {
      if (key.includes("filter")) {
        filters[key] = value;
      }
    }
    return filters;
  }

  // Function to handle filter and sort logic
  let previousState = {
    filters: null,
    sort: null,
  };

  async function handleFilterAndSortChange() {
    const customData = await window.eventStreaming.prepareCommonDetails();
    let filterStateChanged = false;
    let sortStateChanged = false;

    // Handle filters
    if (window.location.href.includes("filter")) {
      const filterParams = getFilterParams();
      if (
        JSON.stringify(filterParams) !== JSON.stringify(previousState.filters)
      ) {
        customData["appliedFilters"] = filterParams;
        customData["sortName"] = previousState.sort;
        previousState.filters = filterParams;
        filterStateChanged = true;
      }
    }

    // Handle sort
    if (window.location.search.includes("sort_by=")) {
      const sortName = new URLSearchParams(window.location.search).get(
        "sort_by",
      );
      if (sortName !== previousState.sort) {
        customData["appliedFilters"] = getFilterParams();
        customData["sortName"] = sortName;
        previousState.sort = sortName;
        sortStateChanged = true;
      }
    }

    if (filterStateChanged) {
      window.eventStreaming.dispatchCustomEvent("APPLY_FILTER", customData);
    }

    if (sortStateChanged) {
      window.eventStreaming.dispatchCustomEvent("APPLY_SORT", customData);
    }
  }

  // Listen for popstate event to handle browser navigation
  window.addEventListener("popstate", handleFilterAndSortChange);

  // MutationObserver to detect changes to the URL query parameters
  const observer = new MutationObserver(() => {
    handleFilterAndSortChange();
  });

  // Observe changes to the URL
  observer.observe(document, { subtree: true, childList: true });

  // Initial call to handle filter and sort logic on page load
  handleFilterAndSortChange();

  //Variant Selected - VARIANT_SELECTED
  document.addEventListener("on:variant:change", async (e) => {
    const customData = await window.eventStreaming.prepareCommonDetails();
    const productContext = getProductContext();
    applyProductData(customData, productContext);
    if (productContext.selectedVariant) {
      customData["selectedVariant"] = productContext.selectedVariant;
    }
    customData["variant"] = e.detail.variant;
    customData["selectedOptions"] = e.detail.selectedOptions;
    customData["selectedQuantity"] = parseInt(
      document.getElementById("quantity")?.value || 1,
    );
    customData["availableOfferIds"] = await window.allOffers.pdpOffers.map(
      (offer) => offer.offerId,
    );
    window.eventStreaming.dispatchCustomEvent("VARIANT_SELECTED", customData);
  });

  // //Add To Cart - ATC
  // document.querySelectorAll('.add-to-cart').forEach((button) => {
  //     button.addEventListener('click', async (e) => {
  //         const customData = await window.eventStreaming.prepareCommonDetails();
  //         customData['product'] = await window.customProductData.product; // need to implement another helper function to get product from liquid file
  //         customData['selectedOptions'] = e.detail.selectedOptions;
  //         customData['selectedQuantity'] = parseInt(document.getElementById('quantity').value);
  //         customData['availableOfferIds'] = await window.allOffers.pdpOffers.map((offer) => offer.offerId);
  //         window.eventStreaming.dispatchCustomEvent('ATC', customData);
  //     })
  // });

  //Cart Open - CART_OPEN
  document.addEventListener("on:cart-drawer:after-open", async (e) => {
    const customData = await window.eventStreaming.prepareCommonDetails();
    customData["cartMode"] = getCartMode(e?.detail);
    try {
      const cart = await fetch("/cart.js").then((response) => response.json());
      customData["cart"] = cart;
      let cartObject = flattenCart(cart);
      if (cartObject && typeof cartObject === "object") {
        Object.keys(cartObject).forEach((key) => {
          customData["cart_" + key] = cartObject[key];
        });
      }
      customData["cartTotal"] = cart.total_price;
      customData["availableOfferIds"] = await (window.allOffers?.pdpOffers || []).map(
        (offer) => offer.offerId,
      );
      window.eventStreaming.dispatchCustomEvent("CART_OPEN", customData);
    } catch (error) {
      console.error("Data pipeline broken as cart.js fetch failed", error);
    }
  });

  //Cart Close - CART_CLOSE
  document.addEventListener("on:cart-drawer:after-close", async (e) => {
    const customData = await window.eventStreaming.prepareCommonDetails();
    customData["cartMode"] = getCartMode(e?.detail);
    try {
      const cart = await fetch("/cart.js").then((response) => response.json());
      customData["cart"] = cart;
      let cartObject = flattenCart(cart);
      if (cartObject && typeof cartObject === "object") {
        Object.keys(cartObject).forEach((key) => {
          customData["cart_" + key] = cartObject[key];
        });
      }
      customData["cartTotal"] = cart.total_price;
      customData["availableOfferIds"] = await window.allOffers.pdpOffers.map(
        (offer) => offer.offerId,
      );
      window.eventStreaming.dispatchCustomEvent("CART_CLOSE", customData);
    } catch (error) {
      console.error("Data pipeline broken as cart.js fetch failed", error);
    }
  });

  //Cart Update - CART_UPDATE
  window.addEventListener("cart_changed", async (e) => {
    const cartMode = getCartMode(e?.detail);
    if (cartMode === "react") {
      // React emits its own normalized event. The legacy path below is only
      // responsible for legacy cart mode, preventing duplicate ATCs.
      window.eventStreaming.prepareCommonDetails();
      return;
    }
    const customData = await window.eventStreaming.prepareCommonDetails();
    customData["cartMode"] = cartMode;
    try {
      const cart = await fetch("/cart.js").then((response) => response.json());
      customData["cart"] = cart;
      let cartObject = flattenCart(cart);
      if (cartObject && typeof cartObject === "object") {
        Object.keys(cartObject).forEach((key) => {
          customData["cart_" + key] = cartObject[key];
        });
      }
      customData["itemsRemoved"] = e.detail.removed;
      customData["itemsAdded"] = e.detail.added;
      customData["quantityChanges"] = e.detail.quantityChanges || [];
      customData["cartTransitionId"] = e.detail.cartTransitionId || null;
      customData["cartTotal"] = cart.total_price;
      customData["availableOfferIds"] = await (window.allOffers?.pdpOffers || []).map(
        (offer) => offer.offerId,
      );
      window.eventStreaming.dispatchCustomEvent("CART_UPDATE", customData);

      // ATC is based on cart additions: newly-created line-item keys and
      // quantity increases on existing lines. Quantity decreases remain
      // CART_UPDATE data and must not produce ATC.
      const addedItems = (customData["itemsAdded"] || []).map((item) => ({
        ...item,
        quantityAdded: item.quantity,
      }));
      const increasedItems = (customData["quantityChanges"] || [])
        .filter((item) => item.quantity > item.previousQuantity)
        .map((item) => ({
          ...item,
          quantityAdded: item.quantity - item.previousQuantity,
        }));
      const atcItems = [...addedItems, ...increasedItems];
      if (atcItems.length > 0) {
        const atcContext = e.detail.atcContext || {};
        const productContext = getProductContext();
        applyProductData(customData, productContext);
        if (productContext.productCsId) {
          customData["productCsId"] = productContext.productCsId;
        }
        if (productContext.selectedVariant) {
          customData["selectedVariant"] = productContext.selectedVariant;
        }
        customData["selectedOptions"] = e.detail.selectedOptions;
        customData["selectedQuantity"] = parseInt(
          document.getElementById("quantity")?.value || 1,
        );
        customData["source"] = atcContext.source || "unknown";
        customData["actionId"] = atcContext.actionId || null;
        customData["newLineItemCount"] = addedItems.length;
        customData["items"] = atcItems.map((item) => ({
          lineItemKey: item.key,
          productId: item.product_id,
          variantId: item.variant_id,
          quantityAdded: item.quantityAdded,
          title: item.product_title,
          price: item.price,
          sku: item.sku,
          properties: item.properties || {},
        }));
        window.eventStreaming.dispatchCustomEvent("ATC", customData);
      }
    } catch (error) {
      console.error("Data pipeline broken as cart.js fetch failed", error);
    }
  });

  //Checkout Clicked - CHECKOUT_CLICKED
  const handleCheckoutClicked = async (eventDetail) => {
    const customData = await window.eventStreaming.prepareCommonDetails();
    customData["cartMode"] = getCartMode(eventDetail);
    try {
      const cart = await fetch("/cart.js").then((response) =>
        response.json(),
      );
      customData["cart"] = cart;
      let cartObject = flattenCart(cart);
      if (cartObject && typeof cartObject === "object") {
        Object.keys(cartObject).forEach((key) => {
          customData["cart_" + key] = cartObject[key];
        });
      }
      customData["cartTotal"] = cart.total_price;
      customData["availableOfferIds"] = await window.allOffers.pdpOffers.map(
        (offer) => offer.offerId,
      );
      window.eventStreaming.dispatchCustomEvent(
        "CHECKOUT_CLICKED",
        customData,
      );
    } catch (error) {
      console.error("Data pipeline broken as cart.js fetch failed", error);
    }
  };

  document.querySelectorAll("marmeto-checkout-button").forEach((button) => {
    button.addEventListener("click", async () => {
      await handleCheckoutClicked();
    });
  });

  document.addEventListener("dispatch:checkout-clicked", async (e) => {
    await handleCheckoutClicked(e?.detail);
  });

  //Login Initiated - LOGIN_INITIATED
  document.addEventListener("event:login:init", async (e) => {
    const customData = await window.eventStreaming.prepareCommonDetails();
    window.eventStreaming.dispatchCustomEvent("LOGIN_INITIATED", customData);
  });

  //Login Started - LOGIN_STARTED
  document.addEventListener("event:login:start", async (e) => {
    const customData = await window.eventStreaming.prepareCommonDetails();
    customData["loginMethod"] = e.detail.method;
    switch (e.detail.method) {
      case "email":
        customData["email"] = e.detail.email;
        break;
      case "phone":
        customData["phone"] = e.detail.phone;
        break;
      case "google":
        customData["google"] = e.detail.google;
        break;
      case "facebook":
        customData["facebook"] = e.detail.facebook;
        break;
      case "apple":
        customData["apple"] = e.detail.apple;
        break;
      case "truecaller":
        customData["phone"] = e.detail.truecaller;
        break;
      default:
        break;
    }
    window.eventStreaming.dispatchCustomEvent("LOGIN_STARTED", customData);
  });

  //OTP Submitted - OTP_SUBMITTED
  document.addEventListener("event:login:otp", async (e) => {
    const customData = await window.eventStreaming.prepareCommonDetails();
    customData["method"] = e.detail.method;
    customData["phone"] = e.detail.phone;
    customData["otpVerified"] = e.detail.otpVerified;
    window.eventStreaming.dispatchCustomEvent("OTP_SUBMITTED", customData);
  });

  //Login Completed - LOGIN_COMPLETED
  document.addEventListener("event:login:complete", async (e) => {
    const customData = await window.eventStreaming.prepareCommonDetails();
    window.eventStreaming.dispatchCustomEvent("LOGIN_COMPLETED", customData);
  });

  //Search - SEARCH
  document.addEventListener("on:search", async (e) => {
    const customData = await window.eventStreaming.prepareCommonDetails();
    customData["search"] = e.detail;
    window.eventStreaming.dispatchCustomEvent("SEARCH", customData);
  });

  // CHECKOUT PROCESS
  document.addEventListener("CHECKOUT_PROCESS_INTERNAL", async (e) => {
    const customData = await window.eventStreaming.prepareCommonDetails();
    try {
      const cart = await fetch("/cart.js").then((response) => response.json());
      customData["cart"] = cart;
      let cartObject = flattenCart(cart);
      if (cartObject && typeof cartObject === "object") {
        Object.keys(cartObject).forEach((key) => {
          customData["cart_" + key] = cartObject[key];
        });
      }
      customData["juspayRequestId"] = e.detail.requestId;
      customData["juspaySessionId"] = e.detail.sessionId;
      flattenNestedObject(e.detail.data, "otherDetails", customData);
      switch (e.detail.event) {
        case "AddPaymentInfo":
          customData["checkoutEvent"] = "ADD_PAYMENT_INFO";
          break;
        case "AddedAddress":
          customData["checkoutEvent"] = "ADD_ADDRESS";
          break;
        case "UpdatedAddress":
          customData["checkoutEvent"] = "UPDATE_ADDRESS";
          break;
        // case 'Purchase':
        //     customData['checkoutEvent'] = 'ORDER';
        //     customData['shopOrderId'] = e.detail.data.shopOrderId;
        //     customData['totalPrice'] = e.detail.data.totalPrice;
        //     customData['itemCount'] = e.detail.data.itemCount;
        //     customData['items'] = e.detail.data.items;
        //     break;
        case "OrderComplete":
          customData["checkoutEvent"] = "CHECKOUT_COMPLETE";
          break;
        default:
          customData["checkoutEvent"] = e.detail.event;
          break;
      }
      if (!!customData?.checkoutEvent) {
        window.eventStreaming.dispatchCustomEvent("CHECKOUT_PROCESS", customData);
      }
    } catch (error) {
      console.error("Data pipeline broken as cart.js fetch failed", error);
    }
  });

  //Native Checkout Process Events for App
  window.addEventListener("message", (e) => {
    // Handle Breeze Purchase Complete for Analytics
    if (typeof e.data === "string" && e.data?.includes("BREEZE_PURCHASE_COMPLETE")) {
      try {
        const parsed = JSON.parse(e.data);
        if (parsed.type === "BREEZE_PURCHASE_COMPLETE") {
          const { items, orderId, sessionId, totalPrice } = parsed.data;
          
          console.log("🎯 Firing analytics for Breeze purchase:", orderId);
          console.log("🎯 Payload sent by app for Breeze purchase:", parsed);

          const { googlePayload, facebookPayload } =
            window.eventStreaming.buildBreezeAnalyticsPayloads({
              items,
              totalPrice,
              transactionId: orderId,
              shipping: 0,
              eventType: "breeze_purchase",
            });
          
          if (window.gtag) {
            window.gtag('event', 'purchase', googlePayload);
          }

          if (window.fbq) {
            window.fbq(
              'track',
              "Purchase",
              facebookPayload,
              {
                eventID: sessionId ||`breeze_purchase_${orderId}_${Date.now()}`
              }
            );
          }
          
          return;
        }
      } catch (err) {
        console.error("Error handling BREEZE_PURCHASE_COMPLETE:", err);
      }
    }
    
    // Existing native checkout handler
    if (typeof e.data === "string" && e.data?.includes("nativeCheckout")) {
      let { data: response } = JSON.parse(e.data);
      console.log("🎯 Payload sent by app for native checkout:", response);
      const { requestId, payload, checkoutPayloadCopy } = response;
      const { action, event, error, data: payloadData, eventName, sessionId } = payload;
      //Handle Breeze Initiate Checkout for Analytics
      if (event ==="InitiateCheckout") {
        const { googlePayload, facebookPayload } =
          window.eventStreaming.buildBreezeAnalyticsPayloads({
            items: payloadData.items,
            totalPrice: payloadData.totalPrice,
            eventType: "breeze_checkout_initiated",
          });

        if (window.gtag) {
          window.gtag('event', 'begin_checkout', googlePayload);
        }

        if (window.fbq) {
          window.fbq(
            'track',
            "InitiateCheckout",
            facebookPayload,
            {
              eventID: sessionId || `breeze_checkout_${Date.now()}`,
            }
          );
        }
      }
      const eventString = event === "Purchase" ? event : !!error ? "CHECKOUT_BREEZE_ERROR_APP" : event ? event : eventName
      const customEvent = new CustomEvent("CHECKOUT_PROCESS_INTERNAL", {
        detail: { requestId, sessionId, event: eventString, 
          data: {
            response : response ? JSON.stringify(response) : {},
            eventName: eventName || null,
            checkoutPayload: checkoutPayloadCopy ? JSON.stringify(checkoutPayloadCopy) : {},
            attachedData: payloadData ? JSON.stringify(payloadData) : {}
          } 
        }
      });
      document.dispatchEvent(customEvent);
    }
  });
  
  // ==================== ERROR TRACKING ====================
  // Listen for Sentry error captures and dispatch to analytics
  
  /**
   * Dispatch error event with flattened data for analytics
   */
  window.eventStreaming.dispatchErrorEvent = (errorCode, context = {}) => {
    if (!window.CultStoreSentry || !window.CultStoreSentry.getFlattenedErrorData) {
      console.warn('[EventStreaming] Sentry integration not available for error flattening');
      return;
    }
    
    try {
      // Get flattened error data from Sentry
      const flattenedData = window.CultStoreSentry.getFlattenedErrorData(errorCode, context);
      
      // Add event streaming specific fields
      const eventData = {
        ...flattenedData,
        eventTimestamp: new Date().toISOString(),
        eventSource: 'event_streaming',
        sessionId: context.sessionId || flattenedData.sessionDeviceId || 'unknown',
        deviceId: context.deviceId || flattenedData.sessionDeviceId || 'unknown',
      };
      
      // Dispatch to analytics systems
      window.eventStreaming.dispatchCustomEvent('ERROR_TRACKED', eventData);
      
      console.log('[EventStreaming] Error tracked:', errorCode, eventData);
    } catch (err) {
      console.error('[EventStreaming] Error dispatching error event:', err);
    }
  };
  
  /**
   * Track checkout errors
   */
  document.addEventListener('CHECKOUT_ERROR', async (event) => {
    try {
      const customData = await window.eventStreaming.prepareCommonDetails();
      const errorData = {
        ...customData,
        ...event.detail,
        errorType: 'CHECKOUT_ERROR',
      };
      
      window.eventStreaming.dispatchCustomEvent('ERROR_TRACKED', errorData);
    } catch (err) {
      console.error('[EventStreaming] Error tracking checkout error:', err);
    }
  });
  
  /**
   * Track login errors
   */
  document.addEventListener('LOGIN_ERROR', async (event) => {
    try {
      const customData = await window.eventStreaming.prepareCommonDetails();
      const errorData = {
        ...customData,
        ...event.detail,
        errorType: 'LOGIN_ERROR',
      };
      
      window.eventStreaming.dispatchCustomEvent('ERROR_TRACKED', errorData);
    } catch (err) {
      console.error('[EventStreaming] Error tracking login error:', err);
    }
  });
  
  /**
   * Track API errors
   */
  document.addEventListener('API_ERROR', async (event) => {
    try {
      const customData = await window.eventStreaming.prepareCommonDetails();
      const errorData = {
        ...customData,
        ...event.detail,
        errorType: 'API_ERROR',
      };
      
      window.eventStreaming.dispatchCustomEvent('ERROR_TRACKED', errorData);
    } catch (err) {
      console.error('[EventStreaming] Error tracking API error:', err);
    }
  });
  
  /**
   * Track SDK errors
   */
  document.addEventListener('SDK_ERROR', async (event) => {
    try {
      const customData = await window.eventStreaming.prepareCommonDetails();
      const errorData = {
        ...customData,
        ...event.detail,
        errorType: 'SDK_ERROR',
      };
      
      window.eventStreaming.dispatchCustomEvent('ERROR_TRACKED', errorData);
    } catch (err) {
      console.error('[EventStreaming] Error tracking SDK error:', err);
    }
  });
  
  /**
   * Track silent failures
   */
  document.addEventListener('SILENT_FAILURE', async (event) => {
    try {
      const customData = await window.eventStreaming.prepareCommonDetails();
      const errorData = {
        ...customData,
        ...event.detail,
        errorType: 'SILENT_FAILURE',
      };
      
      window.eventStreaming.dispatchCustomEvent('ERROR_TRACKED', errorData);
    } catch (err) {
      console.error('[EventStreaming] Error tracking silent failure:', err);
    }
  });
});
